import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GridApi, GridReadyEvent, RowDragEvent } from 'ag-grid-community'
import {
  ProgressReportByTeamPageConfig,
  VersionReportStatus,
  VersionReportVersionPageRowEntity,
} from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { ReorderRow } from '../../../../../applications/ports/versionReportVersionRepository'
import {
  ProgressReportTable,
  ProgressReportTableContent,
} from '../table/ProgressReportTable'
import { DeliverableTaskPopper } from '../dialog/DeliverableTaskPopper'
import {
  ProgressValue,
  WbsItemAggregateCategory,
} from '../../../../../domain/value-object/ProjectPlanSnapshot'
import { useProjectPlanSnapshotRepository } from '../../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import { aggregatedValueService } from '../../../../../domain/value-object/AggregatedValueVO'
import {
  AggregateField,
  WbsItemType,
} from '../../../../../domain/entity/WbsItemEntity'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import { useTeamReportColumnDef } from '../../hooks/columnDef'
import { useProgressReportDragAndDrop } from '../../hooks/dragAndDrop'
import { useDiscussionActionContext } from '../../context/DiscussionContext'
import { TargetCandidate } from '../../../../../domain/entity/DiscussionEntity'
import { useSaveIndicator } from '../save-indicator/SaveIndicatorProvider'
import { buildGridPillRect } from '../save-indicator/gridPillRect'

type ProgressReportByTeamProps = {
  snapshotUuid: string
  pageUuid: string
  editable: boolean
  config: ProgressReportByTeamPageConfig
  rows: VersionReportVersionPageRowEntity[]
  updateRow: (
    rowUuid: string,
    lockVersion: number,
    key: 'status' | 'comment',
    value: string
  ) => Promise<boolean>
  applyReorderedRows: (
    pageUuid: string,
    reorderPayload: ReorderRow[]
  ) => Promise<void>
}
export const ProgressReportByTeam = ({
  editable,
  snapshotUuid,
  pageUuid,
  config,
  rows,
  updateRow,
  applyReorderedRows,
}: ProgressReportByTeamProps) => {
  const [progressValues, setProgressValues] = useState<ProgressValue[]>([])
  const { pivotByTeamUuids } = useProjectPlanSnapshotRepository()
  const { setTargetCandidates } = useDiscussionActionContext()

  useEffect(() => {
    const candidates: TargetCandidate[] = progressValues.map(v => ({
      uuid: v.uuid,
      name: v.name,
      type: 'TEAM',
    }))
    setTargetCandidates(candidates)
    return () => {
      setTargetCandidates([])
    }
  }, [progressValues, setTargetCandidates])

  const gridApiRef = useRef<GridApi | null>(null)
  const [isGridReady, setIsGridReady] = useState(false)
  const {
    contextRef,
    onRowDragEnter,
    onRowDragMove,
    onRowDragEnd,
    onRowDragLeave,
    skipNextFetchRef,
  } = useProgressReportDragAndDrop({
    gridApi: isGridReady ? gridApiRef.current || undefined : undefined,
    progressValues,
    setProgressValues,
    pageUuid,
    rows,
    applyReorderedRows,
  })
  const onGridReady = useCallback((e: GridReadyEvent) => {
    gridApiRef.current = e.api
    setIsGridReady(true)
  }, [])
  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false
      return
    }
    if (!config.rootUuid || config.tableRows.length === 0) {
      setProgressValues([])
      return
    }
    const fn = async () => {
      const response = await pivotByTeamUuids(
        snapshotUuid,
        config.rootUuid,
        config.tableRows
      )
      setProgressValues(response)
    }
    fn()
  }, [
    config.rootUuid,
    config.tableRows,
    pivotByTeamUuids,
    snapshotUuid,
    skipNextFetchRef,
  ])
  const tableContents = useMemo(
    () =>
      progressValues.map(v => {
        const row = rows.find(r => r.targetUuid === v.uuid)
        return {
          uuid: v.uuid,
          name: v.name,
          status: row?.status || 'SUNNY',
          comment: row?.comment || '',
          scheduledProgressRate:
            aggregatedValueService.getFormattedRatePercentage(
              v.planned,
              v.total,
              WbsItemType.TASK,
              AggregateField.WBS_ITEM_COUNT
            ),
          progressRate: aggregatedValueService.getFormattedRatePercentage(
            v.completed,
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          total: aggregatedValueService.getFormatted(
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          planned: aggregatedValueService.getFormatted(
            v.planned,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          completed: aggregatedValueService.getFormatted(
            v.completed,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          delay: aggregatedValueService.getFormatted(
            v.delay,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          remaining: aggregatedValueService.getFormatted(
            v.remaining,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          wbsItemStatus: v.wbsItemStatus,
          team: v.team,
          progressRateDiff: aggregatedValueService.getFormattedRatePercentage(
            v.plannedCompletedDiff,
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          scheduledStartDate: dateV2Service
            .fromString(v.scheduledDate?.startDate)
            ?.toDateString(),
          scheduledEndDate: dateV2Service
            .fromString(v.scheduledDate?.endDate)
            ?.toDateString(),
          maxChildScheduledEndDate: v.maxChildScheduledEndDate,
          preceding: aggregatedValueService.getFormatted(
            v.preceding,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          unplanned: aggregatedValueService.getFormatted(
            v.unplanned,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
        }
      }),
    [rows, progressValues]
  )
  const ref = useRef<HTMLDivElement>(null)
  const { showSavePill } = useSaveIndicator()
  const updateTableContent = useCallback(
    (
      v: { uuid: string; status: VersionReportStatus; comment: string },
      index: number
    ) => {
      const row = rows.find(r => r.targetUuid === v.uuid)
      if (!row) return
      const onSaved = (saved: boolean) => {
        if (!saved) return
        const rect = buildGridPillRect(ref.current, index)
        showSavePill({ key: row.uuid, rect: rect ?? undefined })
      }
      if (v.status !== row.status) {
        void updateRow(row.uuid, row.lockVersion, 'status', v.status).then(
          onSaved
        )
      } else if (v.comment !== row.comment) {
        void updateRow(row.uuid, row.lockVersion, 'comment', v.comment).then(
          onSaved
        )
      }
    },
    [rows, updateRow, showSavePill]
  )

  const [popperState, setPopperState] = useState<{
    anchorEl: HTMLElement
    clickPosition: { x: number; y: number }
    teamUuid: string
    aggregateType: WbsItemAggregateCategory
  } | null>(null)
  const closePopper = useCallback(() => setPopperState(null), [])
  const handleNumericCellClick = useCallback(
    (
      data: ProgressReportTableContent,
      anchorEl: HTMLElement,
      clickPosition: { x: number; y: number },
      aggregateType: WbsItemAggregateCategory
    ) => {
      if (!config.rootUuid) return
      setPopperState({
        anchorEl,
        clickPosition,
        teamUuid: data.uuid,
        aggregateType,
      })
    },
    [config.rootUuid]
  )
  const handleRowDragEnter = useCallback(
    (event: RowDragEvent) => {
      setPopperState(null)
      onRowDragEnter(event)
    },
    [onRowDragEnter]
  )

  useEffect(() => {
    if (!editable) setPopperState(null)
    gridApiRef.current?.refreshCells({ force: true })
  }, [editable])

  const columns = useTeamReportColumnDef({
    editable,
    updateTableContent,
    onNumericCellClick: handleNumericCellClick,
  })
  return (
    <>
      <ProgressReportTable
        ref={ref}
        columns={columns}
        editable={editable}
        contents={tableContents}
        context={contextRef.current}
        onGridReady={onGridReady}
        onRowDragEnter={handleRowDragEnter}
        onRowDragMove={onRowDragMove}
        onRowDragLeave={onRowDragLeave}
        onRowDragEnd={onRowDragEnd}
      />
      <DeliverableTaskPopper
        anchorEl={popperState?.anchorEl ?? null}
        clickPosition={popperState?.clickPosition}
        rootUuid={config.rootUuid}
        teamUuid={popperState?.teamUuid}
        aggregateType={popperState?.aggregateType}
        aggregateTarget={WbsItemType.TASK}
        snapshotUuid={snapshotUuid}
        onClose={closePopper}
      />
    </>
  )
}
